import { FC, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Controller, useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { Button } from '@/components/ui/button';
import {
    Dialog,
    DialogContent,
    DialogTitle,
    DialogDescription,
    DialogHeader,
    DialogFooter,
    DialogClose,
} from '@/components/ui/dialog';
import { Label } from '@/components/ui/label';
import ESingleSelectPicker from '@/components/everridge/ESingleSelectPicker';
import Cancel_01_Icon from '@/components/icons/Cancel_01_Icon';
import { cn } from '@/lib/utils';
import { useTenantsSelectOptionsApi } from '@/features/superAdmin/features/tenants/hooks/useTenantsSelectOptionsApi';
import { useIntegrationMessagesFilter } from './hooks/useIntegrationMessagesFilter';
import { useIntegrationMessageTypesApi } from './useIntegrationMessageTypesApi';
import type { IntegrationMessageKind } from './integration-message.types';
import {
    getIntegrationMessageTypesForKind,
    hasAggregateFields,
    hasExternalIdField,
    hasTenantField,
} from './integration-message.utils';
import {
    IntegrationMessagesPageFilterFormSchema,
    IntegrationMessagesPageFilterFormData,
} from './validators/IntegrationMessagesPageFilterValidator';

type ProcessedFilterValue = 'any' | 'yes' | 'no';

const toProcessedFilterValue = (isProcessed: boolean | null): ProcessedFilterValue =>
    isProcessed === true ? 'yes' : isProcessed === false ? 'no' : 'any';

const toIsProcessed = (value: ProcessedFilterValue): boolean | null =>
    value === 'yes' ? true : value === 'no' ? false : null;

type Props = {
    kind: IntegrationMessageKind;
    onClose: () => void;
    isMobile?: boolean;
};

const IntegrationMessagesPageFilter: FC<Props> = ({ kind, onClose, isMobile }) => {
    const [isOpen, setIsOpen] = useState(true);
    const { t } = useTranslation();
    const { filter, setFilter } = useIntegrationMessagesFilter(kind);
    const { tenantsSelectData } = useTenantsSelectOptionsApi();
    const { messageTypes } = useIntegrationMessageTypesApi();
    const [processedFilter, setProcessedFilter] = useState<ProcessedFilterValue>(() =>
        toProcessedFilterValue(filter.isProcessed),
    );

    const {
        handleSubmit,
        reset,
        control,
        register,
        formState: { errors },
    } = useForm<IntegrationMessagesPageFilterFormData>({
        resolver: zodResolver(IntegrationMessagesPageFilterFormSchema),
        defaultValues: {
            tenantId: filter.tenantId,
            type: filter.type,
            id: filter.id,
            aggregateId: filter.aggregateId,
            externalId: filter.externalId,
        },
    });

    const tenantSelectOptions = useMemo(
        () =>
            (tenantsSelectData?.items ?? []).map((tenant) => ({
                value: tenant.id,
                label: tenant.name ?? tenant.id,
            })),
        [tenantsSelectData?.items],
    );

    const typeOptions = useMemo(
        () =>
            getIntegrationMessageTypesForKind(kind, messageTypes).map((type) => ({
                value: type,
                label: type,
            })),
        [kind, messageTypes],
    );

    const processedOptions = useMemo(
        () => [
            { value: 'any', label: t('integrationMessages.filters.any') },
            { value: 'yes', label: t('integrationMessages.filters.processedYes') },
            { value: 'no', label: t('integrationMessages.filters.processedNo') },
        ],
        [t],
    );

    const handleClose = () => {
        setIsOpen(false);
        setTimeout(onClose, 300);
    };

    const handleSubmitFilterForm = (form: IntegrationMessagesPageFilterFormData) => {
        setFilter({
            tenantId: form.tenantId,
            type: form.type,
            id: form.id?.trim() || null,
            aggregateId: form.aggregateId?.trim() || null,
            externalId: form.externalId?.trim() || null,
            isProcessed: toIsProcessed(processedFilter),
            skip: 0,
        });
        handleClose();
    };

    const handleResetFilter = () => {
        reset({
            tenantId: null,
            type: null,
            id: null,
            aggregateId: null,
            externalId: null,
        });
        setProcessedFilter('any');
    };

    const inputClassName =
        'rounded-lg border border-lightgray bg-base px-3 py-2 text-sm text-black';

    return (
        <Dialog open={isOpen} onOpenChange={handleClose}>
            <DialogContent>
                <DialogHeader>
                    <DialogTitle>
                        {t('elements.components.filter.title')}
                        <DialogClose
                            className={cn(
                                'ml-auto flex items-center justify-center cursor-pointer',
                                isMobile && 'size-11 border border-popover-100 rounded-full',
                            )}
                        >
                            <Cancel_01_Icon />
                        </DialogClose>
                    </DialogTitle>
                    <DialogDescription />
                </DialogHeader>

                <div className="flex flex-col gap-4">
                    {hasTenantField(kind) && (
                        <div className="flex flex-col gap-2">
                            <Label>{t('integrationMessages.filters.tenant')}</Label>
                            <Controller
                                name="tenantId"
                                control={control}
                                render={({ field }) => (
                                    <ESingleSelectPicker
                                        value={field.value}
                                        onChange={field.onChange}
                                        options={tenantSelectOptions}
                                        placeholder={t(
                                            'main.superAdmin.users.form.tenantFilter.placeholder',
                                        )}
                                        allOptionLabel={t(
                                            'main.superAdmin.users.form.tenantFilter.allTenants',
                                        )}
                                        modal={false}
                                    />
                                )}
                            />
                        </div>
                    )}

                    <div className="flex flex-col gap-2">
                        <Label>{t('integrationMessages.filters.type')}</Label>
                        <Controller
                            name="type"
                            control={control}
                            render={({ field }) => (
                                <ESingleSelectPicker
                                    value={field.value}
                                    onChange={field.onChange}
                                    options={typeOptions}
                                    placeholder={t('integrationMessages.filters.typePlaceholder')}
                                    allOptionLabel={t('integrationMessages.filters.any')}
                                    modal={false}
                                />
                            )}
                        />
                    </div>

                    <div className="flex flex-col gap-2">
                        <Label>{t('integrationMessages.filters.processed')}</Label>
                        <select
                            value={processedFilter}
                            onChange={(event) =>
                                setProcessedFilter(event.target.value as ProcessedFilterValue)
                            }
                            className={inputClassName}
                        >
                            {processedOptions.map((option) => (
                                <option key={option.value} value={option.value}>
                                    {option.label}
                                </option>
                            ))}
                        </select>
                    </div>

                    <div className="flex flex-col gap-2">
                        <Label>{t('integrationMessages.filters.messageId')}</Label>
                        <input {...register('id')} className={inputClassName} />
                        {errors.id && (
                            <p className="text-sm text-destructive">{errors.id.message}</p>
                        )}
                    </div>

                    {hasAggregateFields(kind) && (
                        <div className="flex flex-col gap-2">
                            <Label>{t('integrationMessages.filters.aggregateId')}</Label>
                            <input {...register('aggregateId')} className={inputClassName} />
                            {errors.aggregateId && (
                                <p className="text-sm text-destructive">
                                    {errors.aggregateId.message}
                                </p>
                            )}
                        </div>
                    )}

                    {hasExternalIdField(kind) && (
                        <div className="flex flex-col gap-2">
                            <Label>{t('integrationMessages.filters.externalId')}</Label>
                            <input {...register('externalId')} className={inputClassName} />
                        </div>
                    )}
                </div>

                <DialogFooter className={cn('justify-end', isMobile && 'mt-auto')}>
                    <Button variant="outline" onClick={handleResetFilter}>
                        {t('elements.buttons.reset')}
                    </Button>
                    <Button onClick={handleSubmit(handleSubmitFilterForm)}>
                        {t('elements.buttons.apply')}
                    </Button>
                </DialogFooter>
            </DialogContent>
        </Dialog>
    );
};

export default IntegrationMessagesPageFilter;
